import clsx from 'clsx';
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'tt-ui-kit';
import CalcModal from '../../components/CalcModal/CalcModal';
import { DraftContext, CalculatorsContext, ErrorContext } from '../../context/index';
import styles from './Registration.module.scss';
/* eslint-disable-next-line import/no-named-default */
import Form from '../../components/form/Form';
import RegistrationForm from './RegistrationForm';
import RegistrationProject from './RegistrationProject';
import RegistrationCarbon from './RegistrationCarbon';
import { CALC_TYPES } from '../../constants';
import { formatDate } from '../../utils';
import { getCompanyDrafts, getDraftById, updateDraft, replaceDraft } from '../../api/rest/list';

// Registration page for creating or updating a calculator draft.
// Handles company/project selection, form validation, and draft creation/replacement.
// Uses DraftContext for company/draft info and CalculatorsContext for calculator config.
// Displays modals for warnings and confirmation, manages navigation to calculator steps.

const Registration = () => {
  const [validated, setValidated] = useState(false);
  const [progress, setProgress] = useState(0);
  const [initialDraft, setInitialDraft] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [draftsList, setDraftsList] = useState([]);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isNavigate, setIsNavigate] = useState(false);
  const [company, setCompany] = useState(null);
  const [requiredFields, setRequiredFields] = useState([]);
  const [validity, setValidity] = useState({});

  const [agreeWithReplacing, setAgreeWithReplacing] = useState(null);
  const [choosedAction, setChoosedAction] = useState(null);
  const [years, setYears] = useState([]);
  const [reportYear, setReportYear] = useState(null);
  const { calculatorsList, calculatorName, setDraftData, config } = useContext(CalculatorsContext);
  const {
    factors,
    setDraftId,
    draftId,
    setDraft,
    companies,
    legalTypes,
    industrialGroups,
    setCompanyId,
  } = useContext(DraftContext);
  const { setErrorAlert } = useContext(ErrorContext);

  const navigate = useNavigate();

  // Returns the factor ID for the selected year or the latest available year.
  // Used for carbon calculators to link the correct emission factor collection.
  // For carbon calculators, only one draft per year is allowed.
  const getFactorId = (year = null) => {
    let factorId = null;
    if (year) {
      const factor = factors.find((f) => f.year === year);
      factorId = factor.id;
    }
    if (!year || !factorId) {
      const maxYear = Math.max(...factors.map((f) => f.year));
      const currentYear = new Date().getFullYear();
      const factor = factors.find(
        (f) => f.year === (maxYear < currentYear ? maxYear : currentYear)
      );
      factorId = factor.id;
    }
    return factorId;
  };

  // Loads the draft by ID and sets company/draft state.
  // If not found, shows error and navigates away.
  useEffect(() => {
    if (!draftId) return;
    getDraftById({ id: draftId }).then((res) => {
      const { data: newDraft, success } = res.data;
      if (success) {
        setCompanyId(newDraft.companyId);
        setInitialDraft({ ...newDraft });
        setSelectedDraft({ ...newDraft });
      } else {
        setErrorAlert('Draft was not found', () => navigate('/drafts', { replace: true }));
      }
    });
  }, [draftId]);

  // For carbon calculators (isSingleInYear), set the report year from the draft.
  useEffect(() => {
    if (!initialDraft || !config) return;
    if (config.isSingleInYear && initialDraft.periodReportDate)
      setReportYear(initialDraft.periodReportDate.getFullYear());
  }, [initialDraft, config]);

  // For carbon calculators, ensure the draft has a linked emission factor collection.
  useEffect(() => {
    if (!selectedDraft || !!selectedDraft.ukGhgCollectionId) return;
    const ghgDraft = {
      ...selectedDraft,
      ukGhgCollectionId: getFactorId(),
    };
    setSelectedDraft(ghgDraft);
  }, [selectedDraft]);

  // Loads all drafts for the selected company and calculator type.
  // Used to check for duplicates (carbon: unique per year, others: multiple allowed).
  useEffect(() => {
    if (!initialDraft || !initialDraft.companyId || !initialDraft.type) return;
    getCompanyDrafts({ type: initialDraft.type }, { companyId: initialDraft.companyId }).then(
      (res) => {
        const { data, success } = res.data;
        if (success) {
          setDraftsList(data);
        }
      }
    );
  }, [initialDraft]);

  // Sets the company info for the selected draft.
  useEffect(() => {
    if (!companies || !initialDraft || !legalTypes || !industrialGroups) return;
    setCompany(companies[initialDraft.companyId]);
  }, [initialDraft, companies, legalTypes, industrialGroups]);

  // Collects all years from existing drafts for year selection (carbon calculators).
  useEffect(() => {
    if (!draftsList || !draftsList.length) return;
    const newYears = draftsList.reduce(
      (res, d) => (d.periodReportDate ? res.add(d.periodReportDate.getFullYear()) : res),
      new Set()
    );
    setYears([...newYears]);
  }, [draftsList]);

  // Resets navigation flag on mount.
  useEffect(() => {
    setIsNavigate(false);
  }, []);

  const numberFilter = /^(\d+\.)?\d+$/;

  // Sets required fields for the selected calculator type.
  // Carbon calculators require a unique year, others may have different required fields.
  useEffect(() => {
    if (!calculatorName || !config) return;
    const fields = JSON.parse(config.requiredFields);
    setRequiredFields({
      ...fields,
      reportYear: config.isSingleInYear, //  ? 1 : 0,
    });
  }, [calculatorName, config]);

  // Calculates the total number of required fields for validation.
  useEffect(() => {
    if (!requiredFields) return;
    setValidity({
      value: 0,
      total: Object.keys(requiredFields).reduce(
        (sum, key) =>
          key === 'reportYear' ? sum + requiredFields.reportYear : sum + requiredFields[key].length,
        0
      ),
    });
  }, [requiredFields]);

  // Returns the number of validated required fields for the current draft.
  // Used to enable navigation only when all required fields are filled.
  const validatedFields = () => {
    if (!selectedDraft) return 0;

    let totalValidated = 0;
    totalValidated += requiredFields.numberFields
      ? requiredFields.numberFields.filter((n) => numberFilter.test(selectedDraft[n])).length
      : 0;
    totalValidated += requiredFields.stringFields
      ? requiredFields.stringFields.filter((n) => !!selectedDraft[n]).length
      : 0;
    totalValidated += requiredFields.reportYear && reportYear ? 1 : 0;
    totalValidated += requiredFields.dateFields
      ? requiredFields.dateFields.filter((n) => !Number.isNaN(Date.parse(selectedDraft[n]))).length
      : 0;
    return totalValidated;
  };

  // Updates the validity state when the draft or report year changes.
  useEffect(() => {
    if (!selectedDraft || !config) return;
    setValidity((value) => ({
      value: validatedFields(),
      total: value.total,
    }));
  }, [selectedDraft, reportYear, config]);

  // Validates the draft and updates progress bar.
  // For "Project" calculators, both dates must be filled or both empty.
  useEffect(() => {
    /* For the “Project” calculator, you must fill in both dates or neither */
    const newCheck =
      validity.value === validity.total &&
      (calculatorName !== CALC_TYPES.PROJECT ||
        (calculatorName === CALC_TYPES.PROJECT &&
          !!selectedDraft &&
          !!selectedDraft.periodReportDate === !!selectedDraft.periodReportDateEnd));
    setValidated(newCheck);

    const progressValue = validity.value / validity.total;
    setProgress(
      Number.isNaN(progressValue) || progressValue === 0 ? '1px' : `${progressValue * 100}%`
    );
  }, [validity]);

  // Handles changes to form fields and updates the selected draft.
  const onChange = (e) => {
    const { name, value } = e.target;
    setSelectedDraft((newDraft) => ({
      ...newDraft,
      [name]: value,
    }));
  };

  // Handles changes to date fields and updates the report year.
  // Resets agreement for replacing drafts (carbon calculators).
  const onChangeDate = (e) => {
    const newValue = e?.target?.value;
    setAgreeWithReplacing(false);
    setReportYear(newValue ? newValue.getFullYear() : null);
  };

  // Opens the warning modal when a duplicate draft year is detected (carbon calculators).
  const warningModalOpen = () => {
    setIsWarningModalOpen(true);
  };

  // Checks for duplicate draft years when the report year changes.
  // For carbon calculators, prompts user to replace or load previous draft.
  useEffect(() => {
    if (!reportYear) return;
    const exist = (draftsList || []).find(
      (d) => d.periodReportDate && d.periodReportDate.getFullYear() === reportYear
    );
    if (exist && initialDraft.id === exist.id) return;
    if (exist) {
      warningModalOpen();
    } else {
      setSelectedDraft({ ...selectedDraft, periodReportDate: new Date(reportYear, 0, 1) });
    }
  }, [reportYear]);

  // Navigates to the next calculator step after saving the draft.
  // Sets draftId and draft state, triggers navigation effect.
  const goToNextPage = useCallback(
    (draft = selectedDraft) => {
      if (!draft) return;
      setDraftId(draft.id);
      setDraft({ ...draft });
      setIsNavigate(true);
    },
    [selectedDraft, setDraftId, setDraft]
  );

  // Handles user choice in the warning modal (load previous or replace with new draft).
  // For carbon calculators, only one draft per year is allowed.
  useEffect(() => {
    if (!choosedAction) return;
    if (choosedAction === 'load') {
      const exist = (draftsList || []).find(
        (d) => d.periodReportDate && d.periodReportDate.getFullYear() === reportYear
      );
      setSelectedDraft({ ...exist });
      goToNextPage({ ...exist });
    }
    if (choosedAction === 'new') {
      setSelectedDraft({ ...selectedDraft, periodReportDate: new Date(reportYear, 0, 1) });
      setAgreeWithReplacing(true);
      setChoosedAction(null);
    }
    setIsWarningModalOpen(false);
  }, [choosedAction]);

  // Resets the warning modal and draft selection state.
  const resetChoosing = () => {
    setChoosedAction(null);
    setReportYear(null);
    setSelectedDraft({ ...selectedDraft, periodReportDate: null });
    setAgreeWithReplacing(false);
    setIsWarningModalOpen(false);
  };

  // Saves the draft to the backend.
  // For carbon calculators, replacing an existing draft for the same year is allowed.
  // For other calculators, updates the draft or creates a new one.
  const saveDraft = async () => {
    if (choosedAction !== 'load') {
      const input = {
        id: selectedDraft.id,
        annual_turnover: `${selectedDraft.annualTurnover ?? 0}`,
        net_profit: `${selectedDraft.netProfit ?? 0}`,
        year_taxes: `${selectedDraft.yearTaxes ?? 0}`,
        number_unique_products: `${selectedDraft.numberUniqueProducts ?? 0}`,
        annual_productivity_each_position: `${selectedDraft.annualProductivityEachPosition ?? 0}`,
        employees_number: `${selectedDraft.employeesNumber ?? 0}`,
        period_report_date:
          selectedDraft.type === CALC_TYPES.PROJECT
            ? formatDate(selectedDraft.periodReportDate)
            : formatDate(selectedDraft.periodReportDate.getFullYear()),
        period_report_date_end: formatDate(selectedDraft.periodReportDateEnd),
        type: selectedDraft.type,
        uk_ghg_collection_id: selectedDraft.ukGhgCollectionId,
      };

      if (agreeWithReplacing) {
        await replaceDraft({ ...input }, { companyId: selectedDraft.companyId });
      } else {
        await updateDraft(
          {
            ...input,
            name: selectedDraft.name ?? '',
          },
          { companyId: selectedDraft.companyId }
        );
      }
    }
  };

  // Saves the draft and navigates to the next calculator step.
  const startNavigation = async (url) => {
    await saveDraft();
    navigate(url);
  };

  // When navigation is triggered, saves the draft and navigates to the last position or first calculator step.
  useEffect(() => {
    if (!isNavigate) return;
    const url = selectedDraft.lastPosition ?? `/calculators/${calculatorsList[0]}/1`;
    startNavigation(url);
  }, [isNavigate]);

  return (
    <>
      {initialDraft && selectedDraft && (
        <Modal
          open={isWarningModalOpen}
          onClose={resetChoosing}
          title="Start new assessment"
          closeOnlyByControls
        >
          <div className={styles.text}>
            {`You are about to start a new "${initialDraft.name}" assessment.
            Please note that all data from the last saved draft will be lost,
            and the "${selectedDraft.name}" will disappear from the list of all drafts.`}
          </div>
          <div className={clsx(styles.buttonBlock, styles.alertBB)}>
            <Button type="default" onClick={() => resetChoosing()}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => setChoosedAction('load')}>
              Load previous
            </Button>
            <Button type="primary" onClick={() => setChoosedAction('new')}>
              Replace with new
            </Button>
          </div>
        </Modal>
      )}
      <div className={styles.profileContainer}>
        <div className={styles.profile}>
          <div
            className={styles.progressBar}
            style={{
              background: `linear-gradient(to right, #01A0C6 ${progress}, transparent ${progress}, transparent 100%)`,
            }}
          />
          {selectedDraft && (
            <Form headerContent="General Information">
              {company && (
                <div className={styles.infoBlock}>
                  <div>
                    <div className={styles.info}>Company name</div>
                    <div className={styles.text}>{company.companyName}</div>
                  </div>
                  <div>
                    <div className={styles.info}>Address</div>
                    <div className={styles.text}>{company.address1}</div>
                  </div>
                  <div>
                    <div className={styles.info}>Company legal type</div>
                    <div className={styles.text}>{legalTypes[company.legalTypeId].name}</div>
                  </div>
                  <div>
                    <div className={styles.info}>Industrial sector</div>
                    <div className={styles.text}>
                      {industrialGroups[company.industrialGroupId].name}
                    </div>
                  </div>
                  <div>
                    <div className={styles.info}>Established date</div>
                    <div className={styles.text}>{company.establishedDate ?? 'no date'}</div>
                  </div>
                </div>
              )}
              {(calculatorName === CALC_TYPES.LOCAL || calculatorName === CALC_TYPES.GLOBAL) && (
                <RegistrationForm
                  onChange={onChange}
                  onChangeDate={onChangeDate}
                  isDisabled={!draftId}
                  numberFilter={numberFilter}
                  data={selectedDraft}
                  years={years}
                  reportYear={reportYear}
                  dateDisable={!!initialDraft.periodReportDate}
                  factorList={factors}
                />
              )}
              {calculatorName === CALC_TYPES.CARBON && (
                <RegistrationCarbon
                  onChange={onChange}
                  onChangeDate={onChangeDate}
                  openDisclaimer={() => {}}
                  isDisabled={!draftId}
                  numberFilter={numberFilter}
                  data={selectedDraft}
                  years={years}
                  reportYear={reportYear}
                  dateDisable={!!initialDraft.periodReportDate}
                  factorList={factors}
                />
              )}
              {calculatorName === CALC_TYPES.PROJECT && (
                <RegistrationProject
                  onChange={onChange}
                  openDisclaimer={() => () => {}}
                  isDisabled={!draftId}
                  numberFilter={numberFilter}
                  data={selectedDraft}
                  dateDisable={!!initialDraft.periodReportDate}
                  factorList={factors}
                />
              )}
            </Form>
          )}
          {/* <PartialLoader loading={!draft} isLightContainer /> */}
          <div className={clsx(styles.navigation, styles.single)}>
            <Button
              variant="contained"
              onClick={() => goToNextPage(selectedDraft)}
              disabled={!validated}
            >
              NEXT
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Registration;
